import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, Minus } from "@phosphor-icons/react";
import { useState } from "react";
import { faqs } from "@/data/faqs";
import { dogFriendly } from "@/data/dogFriendly";
import { EvChargerDetails } from "@/components/site/EvChargerDetails";
import { HostBadges } from "@/components/site/HostBadges";
import Seo, { SITE } from "@/components/site/Seo";
import { faqJsonLd, breadcrumbJsonLd } from "@/lib/schema";

function Item({ item, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.04 }}
      className="border-b border-emerald/12"
    >
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-6 py-7 text-left group"
      >
        <span className="font-display text-2xl md:text-3xl text-emerald group-hover:text-sunset transition-colors duration-300">{item.q}</span>
        <span className="shrink-0 w-10 h-10 rounded-full border border-emerald/20 grid place-items-center text-emerald group-hover:bg-sunset group-hover:border-sunset group-hover:text-sand transition-colors duration-300">
          {open ? <Minus size={18} /> : <Plus size={18} />}
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p className="text-emerald-soft text-lg leading-relaxed font-light pb-8 max-w-2xl">{item.a}</p>
        {item.cta && (
          <div className="pb-8">
            <Link
              to={item.cta.to}
              data-testid={`faq-cta-${index}`}
              className="group inline-flex items-center gap-2 rounded-full bg-sunset px-6 py-3 text-sand text-sm font-medium transition-colors duration-300 hover:bg-emerald"
            >
              {item.cta.label} <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function EvItem({ index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.04 }}
      className="border-b border-emerald/12"
    >
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-6 py-7 text-left group"
      >
        <span className="font-display text-2xl md:text-3xl text-emerald group-hover:text-sunset transition-colors duration-300">Do your homes have EV charging?</span>
        <span className="shrink-0 w-10 h-10 rounded-full border border-emerald/20 grid place-items-center text-emerald group-hover:bg-sunset group-hover:border-sunset group-hover:text-sand transition-colors duration-300">
          {open ? <Minus size={18} /> : <Plus size={18} />}
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <div data-testid="faq-ev-panel" className="pb-8 max-w-2xl">
          <p className="text-emerald-soft text-lg leading-relaxed font-light">
            All three of our Wilton Manors homes at 1425 NE 24th St share an Emporia Pro Level 2 charger — free for guests, with no apps, no accounts and no per-kWh billing. Plug in when you're back for the evening and wake up to a full battery.
          </p>
          <div className="mt-6">
            <EvChargerDetails />
          </div>
          <p className="text-emerald-soft font-light mt-6 leading-relaxed">
            The charger is shared by the three homes on the property, first-come. If you rely on a full charge every morning, tell us when you book and we'll make sure it works around you.
          </p>
          <Link
            to="/wilton-manors-ev-charging-vacation-rentals"
            data-testid="faq-ev-link"
            className="group mt-6 inline-flex items-center gap-2 rounded-full bg-sunset px-6 py-3 text-sand text-sm font-medium transition-colors duration-300 hover:bg-emerald"
          >
            See our EV-charging rentals <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function FAQ() {
  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="FAQ | Double Rainbow Suites — Wilton Manors & Fort Lauderdale Rentals"
        description="Answers to common questions about Double Rainbow Suites short-term & vacation rentals in Wilton Manors and Fort Lauderdale: booking, check-in times, pet policy, parking, monthly stays and co-hosting / property management support for owners."
        canonical={`${SITE}/faq`}
        jsonLd={[
          faqJsonLd([...faqs, {
            q: "Do your homes have EV charging?",
            a: "Yes — all three Wilton Manors homes at 1425 NE 24th St share an Emporia Pro Level 2 charger, free for guests: up to 11.5 kW, J1772 plug with a NACS/Tesla adapter provided, 25 ft cable.",
          }]),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "FAQ", path: "/faq" },
          ]),
        ]}
      />
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="overline text-sunset mb-5">Guest questions</p>
          <h1 className="font-display text-5xl md:text-8xl leading-[0.88] tracking-tight">
            Everything you<br />need to know.
          </h1>
        </motion.div>
        <HostBadges className="mt-10" />

        <div className="mt-16" data-testid="faq-list">
          {faqs.map((f, i) => (
            <Item key={i} item={f} index={i} />
          ))}
          <EvItem index={faqs.length} />
        </div>

        {/* DOG-FRIENDLY GUIDE */}
        <motion.section
          data-testid="faq-dog-friendly"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-20 md:mt-24"
        >
          <p className="overline text-sunset mb-5">Explore with your pup</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.9] tracking-tight mb-6">
            Dog-friendly things to do in Fort Lauderdale &amp; Wilton Manors.
          </h2>
          <p className="text-emerald-soft text-lg font-light max-w-2xl leading-relaxed">
            Staying at one of our{" "}
            <Link to="/wilton-manors-pet-friendly" className="text-sunset hover:text-emerald transition-colors duration-300 underline decoration-sunset/30 underline-offset-4">
              pet-friendly Wilton Manors rentals
            </Link>
            ? Here's where we send our four-legged guests. Distances are from our Wilton Manors homes at 1425 NE 24th St — and since rules can change, it's worth a quick peek at the official links before you go.
          </p>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-12 mt-12">
            {dogFriendly.map((group, gi) => (
              <div key={group.h} data-testid={`dog-group-${gi}`}>
                <h3 className="font-display text-2xl text-emerald mb-4">{group.h}</h3>
                <ul className="space-y-4">
                  {group.items.map((v) => (
                    <li key={v.name} className="text-emerald-soft font-light leading-relaxed">
                      <a
                        href={v.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-sunset hover:text-emerald transition-colors duration-300 underline decoration-sunset/30 underline-offset-4"
                      >
                        {v.name}
                      </a>
                      <span className="text-emerald/50 text-sm"> · {v.distance}</span> — {v.detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-emerald-soft font-light mt-12 max-w-2xl leading-relaxed">
            For restaurants, nightlife and the rest of the neighborhood, our{" "}
            <Link to="/wilton-manors-guide" className="text-sunset hover:text-emerald transition-colors duration-300 underline decoration-sunset/30 underline-offset-4">
              Wilton Manors guide
            </Link>{" "}
            maps it all out.
          </p>
        </motion.section>

        <div className="mt-16 border border-emerald/10 rounded-2xl p-10 bg-sand-2 text-center">
          <h2 className="font-display text-3xl md:text-4xl mb-3">Still curious?</h2>
          <p className="text-emerald-soft mb-6">We answer messages in minutes — ask us anything.</p>
          <Link to="/contact" data-testid="faq-contact-btn" className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald">
            Contact us <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </div>
      </div>
    </main>
  );
}
